import {
  Rectangle,
  WorkflowSelectService,
  type FlowNodeEntity,
  type FreeLayoutPluginContext,
} from '@flowgram.ai/free-layout-editor';
import { fetchMarkdownContentFromUrl } from '$/utils/download';
import { getImageElementByURL, getVideoElementByURL } from '$/utils/index';
import { useLanguageStore } from '$/store/languageStore';
import { getMediaSafeAreaSize } from '../hooks/useMediaSafeArea';
import { IMAGE_PANORAMA_RESULT_DISPLAY_SIZE } from '../nodes/ImageNode/panoramaConfig';
import { computeVideoNodeSize } from '../nodes/VideoNode/utils';
import { TVFlowNodeNextTypeEnum } from '../config/node.config';
import { createDirectorPanoramaMeta } from '../DirectorConsole/state';
import {
  addAudioNode,
  addImageNode,
  addScriptNode,
  addScreenplayNode,
  addTextNode,
  addVideoNode,
  type ToLineItem,
} from './addNode';
import {
  getNodeCanvasBounds,
  type CanvasNodeBounds,
  type CanvasNodeSize,
} from './canvasGridPosition';
import { getViewportCenterCanvasPosition } from './teamFlowClipper/teamFlowClipperViewport';
import { roboneoNodeCreate, type ContentSource, type NodeSource } from './index';

export interface TeamFlowAssetItem {
  assetId?: number | string;
  id?: string;
  fileExtension?: string;
  mediaLibraryItemId?: number | string;
  materialId?: number | string;
  materialType: string;
  mediaUrl: string;
  coverUrl?: string;
  content?: string;
  raw?: Record<string, any>;
  source?: 'upload' | 'generation' | 'paste' | 'material';
}

interface SeedancePortraitAssetMeta {
  assetGroupId?: string;
  folderId?: string;
  itemId?: string;
}

interface SeedanceComplianceAssetMeta extends SeedancePortraitAssetMeta {
  mediaType?: 'image' | 'video' | 'audio';
  source?: string;
}

export interface TeamFlowMaterialItem {
  content?: string;
  cover?: string;
  coverUrl?: string;
  duration?: string;
  id: string;
  mediaUrl?: string;
  raw?: Record<string, any>;
  title: string;
  type: string;
}

interface AddAssetNodesParams {
  ctx: FreeLayoutPluginContext;
  contentSource?: ContentSource;
  items: TeamFlowAssetItem[];
  nodeSource?: NodeSource;
  position?: { x: number; y: number; };
  /** 手动应用到画布的入口需要把本次创建的节点设为当前选区。 */
  selectNodes?: boolean;
  toLines?: ToLineItem[];
}

interface TeamFlowAssetNodeSize {
  width: number;
  height: number;
}

const TEAM_FLOW_ASSET_NODE_GAP_X = 32;
const TEAM_FLOW_ASSET_NODE_GAP_Y = 48;
const TEAM_FLOW_ASSET_CENTER_COLLISION_MAX_STEPS = 50;
const TEAM_FLOW_ASSET_NODE_COLLISION_PADDING = 16;
/** 同一批横排节点 top 坐标容差（px）。 */
const TEAM_FLOW_ASSET_ROW_Y_TOLERANCE = 16;
const TEAM_FLOW_TEXT_NODE_DEFAULT_SIZE = 240;
const TEAM_FLOW_TEXT_NODE_HEADER_HEIGHT = 32;
const TEAM_FLOW_AUDIO_NODE_WIDTH = 240;
const TEAM_FLOW_AUDIO_NODE_HEIGHT = 160;
const TEAM_FLOW_MEDIA_NODE_HEADER_HEIGHT = 40;
const TEAM_FLOW_ASSET_NODE_LAYOUT_STABLE_FRAMES = 2;
const TEAM_FLOW_ASSET_NODE_LAYOUT_MAX_WAIT_MS = 3000;
const TEAM_FLOW_MARKDOWN_TEXT_ASSET_MATERIAL_TYPES = new Set(['document', 'markdown']);
const TEAM_FLOW_PLAIN_TEXT_ASSET_MATERIAL_TYPES = new Set(['text', 'prompt']);
const TEAM_FLOW_SCRIPT_ASSET_MATERIAL_TYPES = new Set(['script']);
const TEAM_FLOW_SCRIPT_ASSET_HINTS = new Set(['script', 'script_node', 'screenplay', '脚本']);
const TEAM_FLOW_TEXT_FILE_EXTENSIONS = new Set(['md', 'markdown', 'txt']);
const TEAM_FLOW_MEDIA_ASSET_MATERIAL_TYPES = new Set(['image', 'video', 'audio']);

/** 新剧本历史记录有完整 episodes，旧单文件脚本仍走原脚本节点。 */
function isScreenplayHistoryAsset(item?: TeamFlowAssetItem): boolean {
  return item?.materialType?.toLowerCase() === 'script'
    && Array.isArray(item.raw?.episodes);
}
export const External_Media_Storeage_Key = 'team_flow_external_media_storage_key';
export type ExternalCacheDataItem = TeamFlowAssetItem;

function resolveAssetName(item: TeamFlowAssetItem) {
  const raw = item.raw ?? {};
  return raw.name?.trim?.() || raw.file_name?.trim?.() || undefined;
}

/** 读取资产库条目的资产 id；资产库素材条目带 materialId 时不再把 raw.id 当作 assetId。 */
function resolveAssetId(item: TeamFlowAssetItem) {
  if (resolveMediaLibraryItemId(item) != null) {
    return undefined;
  }
  const raw = item.raw ?? {};
  const value = item.assetId ?? raw.assetId ?? raw.asset_id ?? (item.materialId == null ? raw.id : undefined);
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : undefined;
}

/** 读取新版素材库 item_id，避免与历史资产库 assetId 语义混用。 */
function resolveMediaLibraryItemId(item: TeamFlowAssetItem) {
  const raw = item.raw ?? {};
  const value = item.mediaLibraryItemId ?? raw.mediaLibraryItemId ?? raw.media_library_item_id;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : undefined;
}

/** 读取素材条目的素材 id，官方资产使用明确的 official_asset_material_id，避免与 asset_id 混用。 */
function resolveMaterialId(item: TeamFlowAssetItem) {
  const raw = item.raw ?? {};
  const value = item.materialId ?? raw.official_asset_material_id;
  if (value == null || value === '') {
    return undefined;
  }
  return value;
}

/** 从人像素材 raw 信息里读取活体资产组，供落画布节点和生成参数复用。 */
function resolveSeedancePortraitAssetMeta(item: TeamFlowAssetItem): SeedancePortraitAssetMeta | undefined {
  const raw = item.raw ?? {};
  const portrait = raw.seedancePortrait && typeof raw.seedancePortrait === 'object'
    ? raw.seedancePortrait as Record<string, any>
    : {};
  const assetGroupId = portrait.assetGroupId ?? portrait.group_id ?? raw.asset_group_id ?? raw.group_id;
  if (!assetGroupId) return undefined;
  return {
    assetGroupId: String(assetGroupId),
    folderId: portrait.folderId ?? portrait.folder_id ?? raw.folder_id,
    itemId: portrait.itemId ?? portrait.item_id ?? raw.item_id,
  };
}

/** 从合规素材库 raw 信息里读取已过审标记，插入画布时直接写入节点合规状态。 */
function resolveSeedanceComplianceAssetMeta(item: TeamFlowAssetItem): SeedanceComplianceAssetMeta | undefined {
  const raw = item.raw ?? {};
  const compliance = raw.seedanceCompliance && typeof raw.seedanceCompliance === 'object'
    ? raw.seedanceCompliance as Record<string, any>
    : {};
  const portraitMeta = resolveSeedancePortraitAssetMeta(item);
  if (!portraitMeta && compliance.source !== 'seedanceComplianceLibrary') return undefined;
  return {
    ...portraitMeta,
    itemId: compliance.itemId ?? compliance.item_id ?? portraitMeta?.itemId ?? raw.item_id,
    mediaType: compliance.mediaType ?? compliance.media_type,
    source: compliance.source,
  };
}

/** 构造合规库插入节点的成功态，避免已过审素材再次展示手动校验菜单。 */
function createSeedanceComplianceSuccessState(
  item: TeamFlowAssetItem,
  mediaType: 'image' | 'video' | 'audio',
  url: string,
) {
  const complianceMeta = resolveSeedanceComplianceAssetMeta(item);
  if (!complianceMeta) return undefined;
  return {
    status: 'success',
    mediaType,
    url,
    checkedAt: Date.now(),
    assetGroupId: complianceMeta.assetGroupId,
    folderId: complianceMeta.folderId,
    itemId: complianceMeta.itemId,
  };
}

/** 判断值是否为普通对象，供素材库 gen_params 中的 mcpInfo 读取复用。 */
function isPlainRecord(value: unknown): value is Record<string, any> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** 安全解析新版素材库 gen_params，兼容后端返回字符串或已解析对象。 */
function parseMediaLibraryGenParams(value: unknown): Record<string, any> | undefined {
  if (isPlainRecord(value)) {
    return value;
  }
  if (typeof value !== 'string' || !value.trim()) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(value);
    return isPlainRecord(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

/** 从素材库 gen_params 中恢复 mcpInfo；只有 parameters 时补一个最小 mcpInfo，供节点详情展示提示词。 */
function resolveMediaLibraryMcpInfo(genParams?: Record<string, any>) {
  const rawMcpInfo = [
    genParams?.mcpInfo,
    genParams?.mcp_info,
    genParams?.node_data?.mcpInfo,
    genParams?.node_data?.mcp_info,
  ].find(isPlainRecord);
  const fallbackParameters = [
    rawMcpInfo?.parameters,
    genParams?.parameters,
    genParams?.node_data?.parameters,
  ].find(isPlainRecord);
  if (!rawMcpInfo && !fallbackParameters) {
    return undefined;
  }
  const baseMcpInfo = rawMcpInfo ?? {};
  const parameters = fallbackParameters ?? {};
  return {
    ...baseMcpInfo,
    api_name: String(baseMcpInfo.api_name ?? genParams?.api_name ?? genParams?.node_data?.api_name ?? ''),
    model_id: String(baseMcpInfo.model_id ?? genParams?.model_id ?? genParams?.node_data?.model_id ?? ''),
    node_model_id: String(
      baseMcpInfo.node_model_id ?? genParams?.node_model_id ?? genParams?.node_data?.node_model_id ?? '',
    ),
    parameters,
  };
}

/** 构造新节点要继承的素材库生成参数；当前只回灌 mcpInfo 到节点数据。 */
function buildMediaLibraryGeneratedNodeData(item: TeamFlowAssetItem): Record<string, any> {
  const raw = item.raw ?? {};
  const genParams = parseMediaLibraryGenParams(raw.gen_params ?? raw.genParams);
  const mcpInfo = resolveMediaLibraryMcpInfo(genParams);
  return mcpInfo ? { mcpInfo } : {};
}

/** 识别旧历史记录弹窗插入的资产，供后续“保存到资产库”埋点回读 location=history_list。 */
function resolveAssetSaveLocationFromInsertedItem(
  item: TeamFlowAssetItem,
  nodeSource?: NodeSource,
): 'history_list' | undefined {
  if (nodeSource !== 'edit_asset_click') return undefined;
  if (resolveMediaLibraryItemId(item) != null || resolveMaterialId(item) != null) return undefined;
  return 'history_list';
}

/** 文本/脚本类资产复用资产库弹窗的正文读取方法，写入节点 value。 */
async function resolveTextValue(item: TeamFlowAssetItem) {
  if (!item.mediaUrl) {
    return '';
  }
  return fetchMarkdownContentFromUrl(item.mediaUrl);
}

/** 从 URL 路径中提取后缀，忽略查询参数里的签名字段。 */
function getUrlFileExtension(url?: string) {
  const path = (url || '').split(/[?#]/)[0] ?? '';
  const filename = path.split('/').pop() ?? '';
  const ext = filename.includes('.') ? filename.split('.').pop() : '';
  return ext?.toLowerCase?.().trim() || '';
}

/** 规范化素材类型相关提示字段，供脚本/文本兼容识别使用。 */
function normalizeTeamFlowAssetHint(value: unknown): string {
  return String(value ?? '').toLowerCase().trim();
}

/** 从文件名、URL 或普通格式字段中读取后缀。 */
function normalizeTeamFlowAssetFileExtension(value: unknown): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const urlExt = getUrlFileExtension(raw);
  if (urlExt) return urlExt;
  const format = raw.replace(/^\./, '').toLowerCase();
  return /^[a-z0-9]{1,8}$/.test(format) ? format : '';
}

/** 读取资产后缀，兼容弹窗视图字段、后端 file.format 以及 URL 后缀。 */
function getTeamFlowAssetFileExtension(item?: TeamFlowAssetItem) {
  const raw = item?.raw ?? {};
  const extRecord = parseMediaLibraryGenParams(raw.ext);
  const candidates = [
    item?.fileExtension,
    raw.file_extension,
    raw.fileExtension,
    raw.format,
    raw.file_format,
    raw.fileFormat,
    raw.file?.format,
    raw.file?.format_name,
    raw.image?.format,
    raw.video?.format_name,
    raw.audio?.format_name,
    extRecord?.file_extension,
    extRecord?.format,
    raw.name,
    raw.file_name,
    raw.file?.name,
    item?.mediaUrl,
    raw.url,
  ];
  for (const candidate of candidates) {
    const extension = normalizeTeamFlowAssetFileExtension(candidate);
    if (extension) return extension;
  }
  return '';
}

/** 汇总素材类型提示字段，兼容 media library 原始字段和 ext JSON。 */
function getTeamFlowAssetTypeHints(item?: TeamFlowAssetItem): string[] {
  const raw = item?.raw ?? {};
  const extRecord = parseMediaLibraryGenParams(raw.ext);
  return [
    item?.materialType,
    raw.material_type,
    raw.materialType,
    raw.type,
    raw.file_type,
    raw.fileType,
    raw.node_type,
    raw.nodeType,
    raw.cardVariant,
    raw.file?.type,
    raw.file?.format,
    extRecord?.material_type,
    extRecord?.materialType,
    extRecord?.type,
    extRecord?.node_type,
    extRecord?.file_type,
  ].map(normalizeTeamFlowAssetHint).filter(Boolean);
}

/** 判断文档类资产是否为可读取正文的 Markdown/TXT 文件。 */
function isMarkdownTextAsset(item?: TeamFlowAssetItem) {
  const materialType = item?.materialType?.toLowerCase() || '';
  const fileExtension = getTeamFlowAssetFileExtension(item);
  return TEAM_FLOW_MARKDOWN_TEXT_ASSET_MATERIAL_TYPES.has(materialType)
    && TEAM_FLOW_TEXT_FILE_EXTENSIONS.has(fileExtension);
}

/** 判断资产库条目是否应落成文本节点：文本/prompt 直接支持，文档类限定 Markdown/TXT。 */
function isTextAsset(item?: TeamFlowAssetItem) {
  const materialType = item?.materialType?.toLowerCase() || '';
  return !isScriptAsset(item) && (
    TEAM_FLOW_PLAIN_TEXT_ASSET_MATERIAL_TYPES.has(materialType)
    || isMarkdownTextAsset(item)
  );
}

/** 判断资产库条目是否应落成脚本节点。 */
function isScriptAsset(item?: TeamFlowAssetItem) {
  const materialType = item?.materialType?.toLowerCase() || '';
  if (TEAM_FLOW_SCRIPT_ASSET_MATERIAL_TYPES.has(materialType)) return true;
  const hasScriptHint = getTeamFlowAssetTypeHints(item).some((hint) => TEAM_FLOW_SCRIPT_ASSET_HINTS.has(hint));
  if (hasScriptHint) return true;
  const assetName = item ? resolveAssetName(item) || '' : '';
  const fileExtension = getTeamFlowAssetFileExtension(item);
  return TEAM_FLOW_TEXT_FILE_EXTENSIONS.has(fileExtension) && /脚本|script/i.test(assetName);
}

/** 判断资产库条目是否是需要读取正文的文本类资源。 */
function isTextLikeAsset(item?: TeamFlowAssetItem) {
  return isTextAsset(item) || isScriptAsset(item);
}

/** TeamFlow 统一资产落节点支持范围：图片、视频、音频、文本、脚本，以及 .md 文档类文本节点。 */
export function isTeamFlowAssetNodeInsertable(item?: TeamFlowAssetItem) {
  const materialType = item?.materialType?.toLowerCase() || '';
  return TEAM_FLOW_MEDIA_ASSET_MATERIAL_TYPES.has(materialType) || isTextLikeAsset(item);
}

/** 根据资产类型推导 TeamFlow 节点类型；脚本落脚本节点，文档类仅 .md 可落文本节点。 */
export function getAssetNodeType(item?: TeamFlowAssetItem) {
  const materialType = item?.materialType?.toLowerCase() || '';
  if (isScreenplayHistoryAsset(item)) return TVFlowNodeNextTypeEnum.SCREENPLAY_NODE;
  if (materialType === 'audio') {
    return TVFlowNodeNextTypeEnum.AUDIO_NODE;
  }
  if (materialType === 'video') {
    return TVFlowNodeNextTypeEnum.VIDEO_NODE;
  }
  if (materialType === 'image') {
    return TVFlowNodeNextTypeEnum.IMAGE_NODE;
  }
  if (isScriptAsset(item)) {
    return TVFlowNodeNextTypeEnum.SCRIPT_NODE;
  }
  if (isTextAsset(item)) {
    return TVFlowNodeNextTypeEnum.TEXT_NODE;
  }
  return undefined;
}

/**
 * 读取文本节点在 RoboneoTeamFlow 中的默认占位尺寸，和 TextNode 渲染规则保持一致。
 */
function getTeamFlowTextNodeSize(raw: Record<string, any>): TeamFlowAssetNodeSize {
  const width = typeof raw.textNodeWidth === 'number'
    ? raw.textNodeWidth
    : TEAM_FLOW_TEXT_NODE_DEFAULT_SIZE;
  const height = typeof raw.textNodeHeight === 'number'
    ? raw.textNodeHeight
    : TEAM_FLOW_TEXT_NODE_DEFAULT_SIZE;

  return {
    width,
    height: height + TEAM_FLOW_TEXT_NODE_HEADER_HEIGHT,
  };
}

/**
 * 按画布节点真实渲染规则估算媒体内容区尺寸。
 */
function getTeamFlowMediaNodeContentSize(
  raw: Record<string, any>,
  mediaKey: 'image' | 'video',
): { width: number; height: number; } {
  const media = raw[mediaKey] && typeof raw[mediaKey] === 'object'
    ? raw[mediaKey] as Record<string, any>
    : {};
  const rawWidth = typeof media.width === 'number' ? media.width : raw.width;
  const rawHeight = typeof media.height === 'number' ? media.height : raw.height;

  if (mediaKey === 'video') {
    return computeVideoNodeSize({
      width: typeof rawWidth === 'number' ? rawWidth : undefined,
      height: typeof rawHeight === 'number' ? rawHeight : undefined,
    });
  }

  if (typeof rawWidth === 'number' && typeof rawHeight === 'number' && rawWidth > 0 && rawHeight > 0) {
    return getMediaSafeAreaSize({
      media: {
        width: rawWidth,
        height: rawHeight,
        url: typeof media.url === 'string' ? media.url : raw.url,
      },
    });
  }

  return getMediaSafeAreaSize();
}

/**
 * 将媒体内容区尺寸与节点标题栏高度合并，供落点与布局等待回退使用。
 */
function getTeamFlowMediaNodeSize(raw: Record<string, any>, mediaKey: 'image' | 'video'): TeamFlowAssetNodeSize {
  const contentSize = getTeamFlowMediaNodeContentSize(raw, mediaKey);

  return {
    width: contentSize.width,
    height: contentSize.height + TEAM_FLOW_MEDIA_NODE_HEADER_HEIGHT,
  };
}

/**
 * 按 RoboneoTeamFlow 节点渲染规则估算资产节点占位，供批量插入时避让上一节点。
 */
function getTeamFlowAssetNodeSize(item: TeamFlowAssetItem): TeamFlowAssetNodeSize {
  const raw = item.raw ?? {};
  const materialType = item.materialType?.toLowerCase() || '';
  if (isScreenplayHistoryAsset(item)) return { width: 480, height: 344 };
  if (isTextLikeAsset(item)) {
    return getTeamFlowTextNodeSize(raw);
  }
  if (materialType === 'video') {
    return getTeamFlowMediaNodeSize(raw, 'video');
  }
  if (materialType === 'audio') {
    return {
      width: TEAM_FLOW_AUDIO_NODE_WIDTH,
      height: TEAM_FLOW_AUDIO_NODE_HEIGHT,
    };
  }
  if (raw.official_asset_image_key === 'panorama_image') {
    return {
      width: IMAGE_PANORAMA_RESULT_DISPLAY_SIZE.width,
      height: IMAGE_PANORAMA_RESULT_DISPLAY_SIZE.height + TEAM_FLOW_MEDIA_NODE_HEADER_HEIGHT,
    };
  }
  return getTeamFlowMediaNodeSize(raw, 'image');
}

/**
 * 判断两个画布矩形是否重叠，padding 用来给节点之间留出视觉间距。
 */
function isCanvasBoundsOverlapping(
  a: CanvasNodeBounds,
  b: CanvasNodeBounds,
  padding = 0,
): boolean {
  return a.x - padding < b.x + b.width
    && a.x + a.width + padding > b.x
    && a.y - padding < b.y + b.height
    && a.y + a.height + padding > b.y;
}

/**
 * 将 topCenter 落点与节点尺寸换算为画布矩形 bounds。
 */
function getTopCenterCanvasBounds(
  topCenter: { x: number; y: number; },
  nodeSize: CanvasNodeSize,
): CanvasNodeBounds {
  return {
    x: topCenter.x - nodeSize.width / 2,
    y: topCenter.y,
    width: nodeSize.width,
    height: nodeSize.height,
  };
}

/**
 * 读取当前画布所有节点的 bounds，供资产落点碰撞检测使用。
 */
function getAllCanvasNodeBounds(ctx: FreeLayoutPluginContext): CanvasNodeBounds[] {
  return ctx.document.getAllNodes()
    .map((node) => getNodeCanvasBounds(node))
    .filter((bounds): bounds is CanvasNodeBounds => Boolean(bounds));
}

/**
 * 判断 topCenter 落点是否与已有节点占位冲突。
 */
function isTopCenterOccupied(
  topCenter: { x: number; y: number; },
  nodeSize: CanvasNodeSize,
  allBounds: CanvasNodeBounds[],
): boolean {
  const candidate = getTopCenterCanvasBounds(topCenter, nodeSize);
  return allBounds.some((bounds) => isCanvasBoundsOverlapping(
    candidate,
    bounds,
    TEAM_FLOW_ASSET_NODE_COLLISION_PADDING,
  ));
}

/**
 * 找出与目标 bounds 处于同一横排行内的节点（批量添加时 Y 基本一致）。
 */
function getAssetBatchRowPeers(
  rowAnchorBounds: CanvasNodeBounds,
  allBounds: CanvasNodeBounds[],
): CanvasNodeBounds[] {
  return allBounds.filter((bounds) => (
    Math.abs(bounds.y - rowAnchorBounds.y) <= TEAM_FLOW_ASSET_ROW_Y_TOLERANCE
  ));
}

/**
 * 取横排批次首节点的 topCenter.x，使下一批与上一批左对齐而非跟随滚动后的视口中心。
 */
function getAssetBatchRowAnchorTopCenterX(rowBounds: CanvasNodeBounds[]): number {
  const leftmost = rowBounds.reduce((min, bounds) => (
    bounds.x < min.x ? bounds : min
  ), rowBounds[0]);
  return leftmost.x + leftmost.width / 2;
}

/**
 * 视口中心已被占用时，在冲突行下方新增一批；X 与上一批首节点对齐，避免 scrollToView 后视口中心偏移。
 */
function resolveTopCenterBelowOccupiedArea(
  viewportCenter: { x: number; y: number; },
  nodeSize: CanvasNodeSize,
  allBounds: CanvasNodeBounds[],
): { x: number; y: number; } {
  let topCenter = { ...viewportCenter };
  let step = 0;

  while (
    step < TEAM_FLOW_ASSET_CENTER_COLLISION_MAX_STEPS
    && isTopCenterOccupied(topCenter, nodeSize, allBounds)
  ) {
    step += 1;
    const candidate = getTopCenterCanvasBounds(topCenter, nodeSize);
    const overlappingBounds: CanvasNodeBounds[] = [];

    allBounds.forEach((bounds) => {
      if (isCanvasBoundsOverlapping(candidate, bounds, TEAM_FLOW_ASSET_NODE_COLLISION_PADDING)) {
        overlappingBounds.push(bounds);
      }
    });

    if (!overlappingBounds.length) {
      break;
    }

    const rowAnchorBounds = overlappingBounds.reduce((topmost, bounds) => (
      bounds.y < topmost.y ? bounds : topmost
    ), overlappingBounds[0]);
    const rowPeers = getAssetBatchRowPeers(rowAnchorBounds, allBounds);
    const maxBottom = rowPeers.reduce(
      (max, bounds) => Math.max(max, bounds.y + bounds.height),
      rowAnchorBounds.y + rowAnchorBounds.height,
    );

    topCenter = {
      x: getAssetBatchRowAnchorTopCenterX(rowPeers),
      y: maxBottom + TEAM_FLOW_ASSET_NODE_GAP_Y,
    };
  }

  return topCenter;
}

/**
 * 计算批量资产首个节点 topCenter 落点：默认视口正中心；若中心已有节点则整批下移到其下方并与上一批首节点左对齐。
 */
function getInitialAssetBatchTopCenter(
  ctx: FreeLayoutPluginContext,
  nodeType: TVFlowNodeNextTypeEnum,
  nodeSize: CanvasNodeSize,
): { x: number; y: number; } {
  const viewportCenter = getViewportCenterCanvasPosition(ctx)
    ?? ctx.document.getNodeDefaultPosition(nodeType);
  const allBounds = getAllCanvasNodeBounds(ctx);

  if (allBounds.length === 0 || !isTopCenterOccupied(viewportCenter, nodeSize, allBounds)) {
    return viewportCenter;
  }

  return resolveTopCenterBelowOccupiedArea(viewportCenter, nodeSize, allBounds);
}

/** 基于上一节点真实 bounds 计算下一节点 topCenter 落点，批量添加时按水平方向排列。 */
function getNextAssetBatchPosition(
  previousNode: FlowNodeEntity,
  fallbackSize: CanvasNodeSize,
  nextNodeSize: CanvasNodeSize,
): { x: number; y: number; } | undefined {
  const bounds = getNodeCanvasBounds(previousNode, fallbackSize);
  if (!bounds) return undefined;

  return {
    x: bounds.x + bounds.width + TEAM_FLOW_ASSET_NODE_GAP_X + nextNodeSize.width / 2,
    y: bounds.y,
  };
}

/**
 * 等待节点布局高度稳定（图片/视频异步加载后会撑高），再计算下一个落点。
 */
function waitForNodeCanvasBoundsStable(
  node: FlowNodeEntity,
  fallbackSize: CanvasNodeSize,
  options?: { maxWaitMs?: number; stableFrames?: number; },
): Promise<CanvasNodeBounds | undefined> {
  const maxWaitMs = options?.maxWaitMs ?? TEAM_FLOW_ASSET_NODE_LAYOUT_MAX_WAIT_MS;
  const stableFramesRequired = options?.stableFrames ?? TEAM_FLOW_ASSET_NODE_LAYOUT_STABLE_FRAMES;
  const start = performance.now();
  let lastHeight: number | undefined;
  let stableCount = 0;

  return new Promise((resolve) => {
    /**
     * 每帧检查节点 bounds，连续若干帧高度不变则视为布局稳定。
     */
    function tick() {
      const bounds = getNodeCanvasBounds(node, fallbackSize);

      if (bounds) {
        if (lastHeight !== undefined && Math.abs(bounds.height - lastHeight) < 1) {
          stableCount += 1;
        } else {
          stableCount = 0;
        }
        lastHeight = bounds.height;

        if (stableCount >= stableFramesRequired) {
          resolve(bounds);
          return;
        }
      }

      if (performance.now() - start >= maxWaitMs) {
        resolve(bounds ?? getNodeCanvasBounds(node, fallbackSize));
        return;
      }

      requestAnimationFrame(tick);
    }

    requestAnimationFrame(tick);
  });
}

/**
 * 等待批量新增节点的真实选区边界稳定，避免 FlowGram 用未完成布局的 bounds 创建多选框。
 */
function waitForNodesSelectionBoundsStable(
  nodes: FlowNodeEntity[],
  options?: { maxWaitMs?: number; stableFrames?: number; },
): Promise<boolean> {
  const maxWaitMs = options?.maxWaitMs ?? TEAM_FLOW_ASSET_NODE_LAYOUT_MAX_WAIT_MS;
  const stableFramesRequired = options?.stableFrames ?? TEAM_FLOW_ASSET_NODE_LAYOUT_STABLE_FRAMES;
  const start = performance.now();
  let lastBoundsSnapshot = '';
  let stableCount = 0;

  return new Promise((resolve) => {
    /** 连续检查所有节点真实 bounds，尺寸和坐标均有效且不再变化后再创建多选框。 */
    function tick() {
      const boundsList = nodes.map((node) => node.bounds);
      const boundsReady = boundsList.every((bounds) => (
        Number.isFinite(bounds.x)
        && Number.isFinite(bounds.y)
        && Number.isFinite(bounds.width)
        && Number.isFinite(bounds.height)
        && bounds.width > 0
        && bounds.height > 0
      ));
      const boundsSnapshot = boundsReady
        ? boundsList.map((bounds) => [bounds.x, bounds.y, bounds.width, bounds.height].join(',')).join('|')
        : '';

      if (boundsSnapshot && boundsSnapshot === lastBoundsSnapshot) {
        stableCount += 1;
      } else {
        stableCount = 0;
      }
      lastBoundsSnapshot = boundsSnapshot;

      if (stableCount >= stableFramesRequired) {
        resolve(true);
        return;
      }

      if (performance.now() - start >= maxWaitMs) {
        resolve(boundsReady);
        return;
      }

      requestAnimationFrame(tick);
    }

    requestAnimationFrame(tick);
  });
}

/**
 * 上传资源缺少宽高时，从 URL 读取 metadata，减少落点时的尺寸误差。
 */
async function enrichTeamFlowAssetItemMediaSize(item: TeamFlowAssetItem): Promise<TeamFlowAssetItem> {
  const raw = item.raw ?? {};
  const materialType = item.materialType?.toLowerCase() || '';

  if (materialType === 'image') {
    const width = raw.image?.width ?? raw.width;
    const height = raw.image?.height ?? raw.height;
    if (typeof width === 'number' && typeof height === 'number' && width > 0 && height > 0) {
      return item;
    }
    if (!item.mediaUrl) return item;

    try {
      const image = await getImageElementByURL(item.mediaUrl);
      const nextWidth = image.naturalWidth || image.width;
      const nextHeight = image.naturalHeight || image.height;
      if (!nextWidth || !nextHeight) return item;

      return {
        ...item,
        raw: {
          ...raw,
          width: nextWidth,
          height: nextHeight,
          image: {
            ...(raw.image ?? {}),
            width: nextWidth,
            height: nextHeight,
          },
        },
      };
    } catch {
      return item;
    }
  }

  if (materialType === 'video') {
    const width = raw.video?.width ?? raw.width;
    const height = raw.video?.height ?? raw.height;
    if (typeof width === 'number' && typeof height === 'number' && width > 0 && height > 0) {
      return item;
    }
    if (!item.mediaUrl) return item;

    try {
      const video = await getVideoElementByURL(item.mediaUrl);
      if (!video.videoWidth || !video.videoHeight) return item;

      return {
        ...item,
        raw: {
          ...raw,
          width: video.videoWidth,
          height: video.videoHeight,
          video: {
            ...(raw.video ?? {}),
            width: video.videoWidth,
            height: video.videoHeight,
            duration: raw.video?.duration ?? raw.duration ?? video.duration,
          },
        },
      };
    } catch {
      return item;
    }
  }

  return item;
}

/** 读取素材原始数据里的媒体文件对象，兼容新版 pic_file 以及视频/音频文件字段。 */
function getRawMaterialFile(raw: Record<string, any> | undefined, keys: string[]) {
  if (!raw) return undefined;
  for (const key of keys) {
    const value = raw[key];
    if (value && typeof value === 'object') {
      return value as Record<string, any>;
    }
  }
  return undefined;
}

/** 从新版素材文件对象中提取文件名，作为素材名称兜底。 */
function getMaterialFileName(...files: Array<Record<string, any> | undefined>) {
  for (const file of files) {
    if (typeof file?.name === 'string' && file.name.trim() !== '') {
      return file.name;
    }
  }
  return undefined;
}

/** 将资源库素材转换为资产节点数据，并保留运镜 preview 中的视频元信息。 */
export function materialToAssetItem(item: TeamFlowMaterialItem): TeamFlowAssetItem {
  const coverUrl = item.coverUrl || item.cover;
  const picFile = getRawMaterialFile(item.raw, ['pic_file', 'picFile']);
  const preview = getRawMaterialFile(item.raw, ['preview', 'video_file', 'videoFile', 'media_file', 'mediaFile', 'file']);
  const rawName = typeof item.raw?.name === 'string' ? item.raw.name : undefined;
  const fileName = getMaterialFileName(preview, picFile);

  return {
    id: item.id,
    materialId: item.id,
    materialType: item.type,
    mediaUrl: item.mediaUrl || coverUrl || '',
    coverUrl,
    content: item.content,
    source: 'material',
    raw: {
      ...item.raw,
      name: rawName || item.title || fileName,
      duration: item.raw?.duration || preview?.duration || item.duration,
      image: item.type === 'image'
        ? {
          ...picFile,
          width: picFile?.width ?? item.raw?.width,
          height: picFile?.height ?? item.raw?.height,
        }
        : item.raw?.image,
      video: item.type === 'video'
        ? {
          ...preview,
          width: preview?.width ?? item.raw?.width,
          height: preview?.height ?? item.raw?.height,
          duration: preview?.duration ?? item.raw?.duration,
        }
        : item.raw?.video,
    },
  };
}

/**
 * 将跨页面进入 TeamFlow 的外部资产缓存到 sessionStorage，页面初始化后统一走 addAssetNodesToTeamFlow 落节点。
 */
export function addExternalMedias(mediaList: ExternalCacheDataItem[]) {
  sessionStorage.setItem(External_Media_Storeage_Key, JSON.stringify(mediaList));
}

/** 将资产库条目/资产库素材条目加入画布；无生成参数的标记为用户素材节点，带生成参数的还原为生成节点形态。 */
export async function addAssetNodesToTeamFlow({
  ctx,
  contentSource,
  items,
  nodeSource,
  position,
  selectNodes = false,
  toLines,
}: AddAssetNodesParams) {
  const screenplayTitle = useLanguageStore.getState().languageStore.smartcatTranslationText(
    (value) => value.script_1,
  );
  const insertableItems = await Promise.all(
    items.filter(isTeamFlowAssetNodeInsertable).map(enrichTeamFlowAssetItemMediaSize),
  );
  const textValues = await Promise.all(insertableItems.map((item) => (
    isTextLikeAsset(item) && !isScreenplayHistoryAsset(item) ? resolveTextValue(item) : Promise.resolve('')
  )));
  let lastAddedNode: ReturnType<typeof addImageNode> | undefined;
  let previousNodeSize: TeamFlowAssetNodeSize | undefined;
  const addedNodes: FlowNodeEntity[] = [];
  let selectionBoundsReady = false;

  for (let index = 0; index < insertableItems.length; index += 1) {
    const item = insertableItems[index];
    const materialType = item.materialType?.toLowerCase() || '';
    const itemNodeSize = getTeamFlowAssetNodeSize(item);
    const nodeType = getAssetNodeType(item) ?? TVFlowNodeNextTypeEnum.IMAGE_NODE;
    let x: number;
    let y: number;

    if (position) {
      x = position.x + index * 32;
      y = position.y + index * 32;
    } else if (index === 0) {
      ({ x, y } = getInitialAssetBatchTopCenter(ctx, nodeType, itemNodeSize));
    } else if (lastAddedNode && previousNodeSize) {
      // eslint-disable-next-line no-await-in-loop -- 批量插入需要等待上一节点布局稳定后计算下一节点落点
      await waitForNodeCanvasBoundsStable(lastAddedNode, previousNodeSize);
      const nextPosition = getNextAssetBatchPosition(
        lastAddedNode,
        previousNodeSize,
        itemNodeSize,
      );
      if (nextPosition) {
        ({ x, y } = nextPosition);
      } else {
        ({ x, y } = getInitialAssetBatchTopCenter(ctx, nodeType, itemNodeSize));
      }
    } else {
      ({ x, y } = getInitialAssetBatchTopCenter(ctx, nodeType, itemNodeSize));
    }

    const raw = item.raw ?? {};
    const isOfficialAsset = raw.tool_type === 'official_asset_library';
    const name = resolveAssetName(item);
    const assetId = resolveAssetId(item);
    const materialId = resolveMaterialId(item);
    const mediaLibraryItemId = resolveMediaLibraryItemId(item);
    const saveAssetLocation = resolveAssetSaveLocationFromInsertedItem(item, nodeSource);
    const generatedNodeData = {
      ...buildMediaLibraryGeneratedNodeData(item),
      ...(saveAssetLocation ? { saveAssetLocation } : {}),
    };
    // 带生成参数（mcpInfo）的资产回灌为生成节点形态，节点参数栏才能展示保存时的提示词/模型配置；
    // 无生成参数的资产维持用户素材节点，复用上传节点的交互限制。
    const isCustomer = !('mcpInfo' in generatedNodeData);
    const seedanceImageComplianceState = createSeedanceComplianceSuccessState(item, 'image', item.mediaUrl);
    const seedanceVideoComplianceState = createSeedanceComplianceSuccessState(item, 'video', item.mediaUrl);
    const seedanceAudioComplianceState = createSeedanceComplianceSuccessState(item, 'audio', item.mediaUrl);
    let addedNode: ReturnType<typeof addImageNode> | undefined;

    if (materialType === 'image') {
      const panoramaMeta = raw.official_asset_image_key === 'panorama_image'
        ? createDirectorPanoramaMeta()
        : undefined;
      const image = {
        url: item.mediaUrl,
        originUrl: item.mediaUrl,
        ...(name ? { name } : {}),
        width: raw.image?.width ?? raw.width,
        height: raw.image?.height ?? raw.height,
        watermark_url: item.mediaUrl,
        assetId,
        mediaLibraryItemId,
        ...(materialId != null ? { material_id: materialId } : {}),
        ...(item.source ? { source: item.source } : {}),
        ...(seedanceImageComplianceState ? { seedanceCompliance: seedanceImageComplianceState } : {}),
      };
      addedNode = addImageNode(ctx, {
        x,
        y,
        data: {
          ...generatedNodeData,
          isCustomer,
          ...(panoramaMeta ? {
            panoramaMeta,
            panoramaResolution: panoramaMeta.resolution,
          } : {}),
          ...(mediaLibraryItemId != null ? { mediaLibraryItemId } : {}),
          media_list: [image],
          status: 'loaded',
        } as any,
        nodeName: name,
        toLines,
      });
    } else if (materialType === 'video') {
      addedNode = addVideoNode(ctx, {
        x,
        y,
        data: {
          ...generatedNodeData,
          isCustomer,
          ...(mediaLibraryItemId != null ? { mediaLibraryItemId } : {}),
          media_list: item.mediaUrl ? [{
            url: item.mediaUrl,
            ...(name ? { name } : {}),
            width: raw.video?.width ?? raw.width,
            height: raw.video?.height ?? raw.height,
            duration: raw.video?.duration ?? raw.duration,
            cover_url: item.coverUrl,
            watermark_url: item.mediaUrl,
            watermark_cover_url: item.coverUrl,
            assetId,
            mediaLibraryItemId,
            ...(materialId != null ? { material_id: materialId } : {}),
            ...(seedanceVideoComplianceState ? { seedanceCompliance: seedanceVideoComplianceState } : {}),
          }] : [],
        } as any,
        nodeName: name,
        toLines,
      });
    } else if (materialType === 'audio') {
      addedNode = addAudioNode(ctx, {
        x,
        y,
        data: {
          ...generatedNodeData,
          isCustomer,
          ...(mediaLibraryItemId != null ? { mediaLibraryItemId } : {}),
          media_list: item.mediaUrl ? [{
            url: item.mediaUrl,
            ...(name ? { name } : {}),
            duration: raw.audio?.duration ?? raw.duration,
            assetId,
            mediaLibraryItemId,
            ...(materialId != null ? { material_id: materialId } : {}),
            ...(seedanceAudioComplianceState ? { seedanceCompliance: seedanceAudioComplianceState } : {}),
          }] : [],
        } as any,
        nodeName: name,
        toLines,
      });
    } else if (isTextAsset(item)) {
      addedNode = addTextNode(ctx, {
        x,
        y,
        data: {
          ...generatedNodeData,
          isCustomer,
          ...(mediaLibraryItemId != null ? { mediaLibraryItemId } : {}),
          name: name || '文本',
          title: name,
          media_list: [{
            value: textValues[index],
            url: item.mediaUrl,
            assetId,
            mediaLibraryItemId,
            ...(materialId != null ? { material_id: materialId } : {}),
          }],
        },
        nodeName: name,
        toLines,
      });
    } else if (isScreenplayHistoryAsset(item)) {
      if (assetId == null || !Number.isSafeInteger(assetId) || assetId <= 0) {
        throw new Error('剧本历史记录缺少有效的 asset_id');
      }
      const firstUrl = raw.episodes?.[0]?.urls?.[0] || item.mediaUrl || '';
      addedNode = addScreenplayNode(ctx, {
        x,
        y,
        data: {
          name: name || screenplayTitle,
          media_list: firstUrl ? [{ url: firstUrl }] : [],
          screenplay_first_episode_url: firstUrl,
          screenplay_episode_count: raw.episodes.length,
          screenplay_history_restore_asset_id: assetId,
          screenplay_history_restore_pending: true,
        },
        nodeName: name,
        toLines,
      });
    } else if (isScriptAsset(item)) {
      addedNode = addScriptNode(ctx, {
        x,
        y,
        data: {
          ...generatedNodeData,
          isCustomer,
          ...(mediaLibraryItemId != null ? { mediaLibraryItemId } : {}),
          name: name || '脚本',
          title: name,
          media_list: [{
            value: textValues[index],
            url: item.mediaUrl,
            assetId,
            mediaLibraryItemId,
            ...(materialId != null ? { material_id: materialId } : {}),
          }],
        },
        nodeName: name,
        toLines,
      });
    }

    if (addedNode) {
      lastAddedNode = addedNode;
      addedNodes.push(addedNode);
      previousNodeSize = itemNodeSize;
    }

    const trackNodeSource = isOfficialAsset ? 'official_card' : nodeSource;
    const trackContentSource = isOfficialAsset ? 'official_card' : contentSource;
    if (addedNode && trackNodeSource && trackContentSource) {
      roboneoNodeCreate(addedNode, {
        node_source: trackNodeSource,
        content_source: trackContentSource,
      });
    }
  }

  if (selectNodes && addedNodes.length > 0) {
    selectionBoundsReady = await waitForNodesSelectionBoundsStable(addedNodes);
  }

  const scrollBounds = selectNodes && addedNodes.length > 1 && selectionBoundsReady
    ? Rectangle.enlarge(addedNodes.map((node) => node.bounds))
    : lastAddedNode?.bounds;
  if (scrollBounds) {
    await ctx.playground.scrollToView({ bounds: scrollBounds });
  }

  if (selectNodes && addedNodes.length > 0 && selectionBoundsReady) {
    ctx.get(WorkflowSelectService).selection = addedNodes;
    ctx.playground.node.focus();
  }

  return addedNodes;
}

/**
 * 渲染跨页面缓存的外部资产，复用资产弹窗同一套节点创建、类型识别和 Markdown 正文读取逻辑。
 */
export async function renderExternalMedias(ctx: FreeLayoutPluginContext) {
  try {
    const cacheData = sessionStorage.getItem(External_Media_Storeage_Key);
    if (!cacheData) {
      return false;
    }
    const cacheDataList: ExternalCacheDataItem[] = JSON.parse(cacheData);
    if (!cacheDataList.length) {
      sessionStorage.removeItem(External_Media_Storeage_Key);
      return false;
    }

    await addAssetNodesToTeamFlow({
      ctx,
      items: cacheDataList,
      selectNodes: true,
    });
    sessionStorage.removeItem(External_Media_Storeage_Key);
    return true;
  } catch (error) {
    console.error('[renderExternalMedias] render cached media failed', error);
    sessionStorage.removeItem(External_Media_Storeage_Key);
    return false;
  }
}
